// src/lib/offline/appwriteProxy.ts
import { Databases, Functions, Storage, ExecutionMethod } from 'appwrite';
import type { Client } from 'appwrite';
import { appwriteCache } from './appwriteCache';

export const createCachedDatabases = (client: Client) => {
  const db = new Databases(client);
  
  const originalListDocuments = db.listDocuments.bind(db);
  const originalGetDocument = db.getDocument.bind(db);

  // @ts-ignore
  db.listDocuments = async (databaseId: string, collectionId: string, queries?: string[]) => {
    return await appwriteCache.wrapCall(
      'listDocuments',
      { databaseId, collectionId, queries },
      () => originalListDocuments(databaseId, collectionId, queries),
      /**
       * 30 minutos: suficiente para que los listados del dashboard
       * sigan disponibles durante la jornada, pero sin volverse obsoletos.
       */
      30 * 60 * 1000
    );
  };

  // @ts-ignore
  db.getDocument = async (databaseId: string, collectionId: string, documentId: string, queries?: string[]) => {
    return await appwriteCache.wrapCall(
      'getDocument',
      { databaseId, collectionId, documentId, queries },
      () => originalGetDocument(databaseId, collectionId, documentId, queries),
      /**
       * 60 minutos: documentos individuales suelen cambiar menos
       * y es útil mantenerlos más tiempo para navegación offline.
       */
      60 * 60 * 1000
    );
  };

  return db;
};

export const createCachedFunctions = (client: Client) => {
  const fn = new Functions(client);
  const originalCreateExecution = fn.createExecution.bind(fn);

  // @ts-ignore
  fn.createExecution = async (
    functionId: string, 
    body?: string, 
    async?: boolean, 
    path?: string, 
    method?: ExecutionMethod, // 🆕 Usar el tipo correcto de Appwrite
    headers?: object
  ) => {
    // Solo cachear GET
    if (method === ExecutionMethod.GET || !method) { // 🆕 Usar enum
      return await appwriteCache.wrapCall(
        'createExecution',
        { functionId, body, path, method },
        () => originalCreateExecution(functionId, body, async, path, method, headers),
        /**
         * 15 minutos: funciones GET típicas (ej. tiempo de servidor, listados ligeros)
         * pueden tolerar este TTL sin perder frescura crítica.
         */
        15 * 60 * 1000
      );
    }
    
    // POST/PUT/DELETE pasan directo
    return await originalCreateExecution(functionId, body, async, path, method, headers);
  };

  return fn;
};

export const createCachedStorage = (client: Client) => {
  return new Storage(client);
};